import type {
  PartFileStandaloneViewerData,
  PartFileViewerPageData,
} from '@frontend/api';

// Decision-critical: read as bare, un-guarded booleans in render branches
// (shouldShowCAD = hasGeometry, `if (partFile.isRootPart)`, etc.) — must
// always resolve to a real boolean, never undefined-because-wrong-source.
type ViewerDecisionKeys =
  | 'containsVirus'
  | 'isAssembly'
  | 'isManual'
  | 'hasGeometry'
  | 'processingFailed'
  | 'hasAssemblyParentFile'
  | 'isRootPart';

// Identity/URL fields — present and required on both source types already.
type ViewerIdentityKeys =
  | 'uuid'
  | 'filename'
  | 'url'
  | 'standardizedUrl'
  | 'units'
  | 'printUnits';

// Enrichment — Every current reader already guards these
// with `?.`/truthy checks, so no forced default is added (e.g. weight: 0
// would destroy real data, since 0 is a legitimate weight).
type ViewerEnrichmentKeys =
  | 'assemblyParentFileUuid'
  | 'flatFileUuid'
  | 'pmiUrl'
  | 'printPages'
  | 'area'
  | 'volume'
  | 'weight'
  | 'dxfSceneUrl'
  | 'dxfSceneSchemaVersion'
  | 'hasOptimalBoundingBox';

/**
 * Canonical, source-agnostic shape stored in modules/common/viewer's shared
 * entity reducer. Produced by normalizePartFileForViewer from either
 * PartFileStandaloneViewerData or PartFileViewerPageData so that Viewer.tsx,
 * utils.ts, and this reducer's selectors never need to know which raw wire
 * shape a file came from. Fields not in this type (isPrimaryFile, partUuid,
 * technicalDrawingId, forbiddenInterrogationInformation, etc.) are read only
 * from modules/viewerPage/data/reducer.ts's own state, never from here.
 */
export type ViewerPartFile = Pick<
  PartFileViewerPageData,
  ViewerIdentityKeys | ViewerDecisionKeys
> &
  Partial<Pick<PartFileViewerPageData, ViewerEnrichmentKeys>> & {
    assemblyChildFiles: ViewerPartFile[]; // recursive canonical shape, not Pick-able
  };

/** Whatever a raw dispatch site might hand in: either wire shape, or an
 *  already-normalized entity (see usePrintUnitsFeature's re-dispatch case). */
export type RawViewerPartFile =
  | PartFileStandaloneViewerData
  | PartFileViewerPageData
  | ViewerPartFile;

export const normalizePartFileForViewer = (
  input: RawViewerPartFile,
): ViewerPartFile => ({
  ...input,
  isManual: 'isManual' in input ? input.isManual : false,
  hasGeometry: input.hasGeometry ?? false,
  processingFailed: input.processingFailed ?? false,
  hasAssemblyParentFile: input.hasAssemblyParentFile ?? false,
  isRootPart: 'isRootPart' in input ? input.isRootPart : false,
  flatFileUuid: 'flatFileUuid' in input ? input.flatFileUuid : undefined,
  weight: 'weight' in input ? input.weight : undefined,
  assemblyChildFiles: (input.assemblyChildFiles ?? []).map(
    normalizePartFileForViewer,
  ),
});
